import { PageControls, usePagination } from '@/components/shared/pagination/Pagination';
import tableStrings from '@/components/shared/table/table.translations';
import { TableSelectFilter, TableSelectFilterOption } from '@/components/shared/table/TableSelectFilter';
import { strings } from '@/services/translation/strings';

import { FC, FunctionComponent, useMemo, useState } from 'react';
import { RequestRow, RequestStatus, RequestType } from './RequestsTable.types';
import { StatusChip, statusLabels } from './RequestStatusChip';

const columnWidths = {
  dateSubmitted: '16%',
  status: '22%',
  action: '14%'
};
const pageSize = 20;

type StatusFilterValue = RequestStatus | 'All';
type TypeFilterValue = RequestType | 'All';

const STATUS_OPTIONS: TableSelectFilterOption<StatusFilterValue>[] = [
  { label: strings.timeshift.teamMember.requests.filters.status, value: 'All' },
  { label: statusLabels[RequestStatus.Approved], value: RequestStatus.Approved },
  { label: statusLabels[RequestStatus.Pending], value: RequestStatus.Pending },
  { label: statusLabels[RequestStatus.CancelledByEmployee], value: RequestStatus.CancelledByEmployee },
  { label: statusLabels[RequestStatus.CancelledByManager], value: RequestStatus.CancelledByManager },
  { label: statusLabels[RequestStatus.Denied], value: RequestStatus.Denied }
];

const TYPE_OPTIONS: TableSelectFilterOption<TypeFilterValue>[] = [
  { label: strings.timeshift.teamMember.requests.filters.requestType, value: 'All' },
  { label: RequestType.LeaveRequest, value: RequestType.LeaveRequest },
  { label: RequestType.ScheduleChange, value: RequestType.ScheduleChange },
  { label: RequestType.AvailabilityChange, value: RequestType.AvailabilityChange }
];

function formatDateSubmitted(dateSubmitted: string): string {
  return new Date(dateSubmitted).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

const RequestsTableHeader: FC = () => (
  <thead>
    <tr>
      <th
        className="px-6 pb-2 pt-4 text-left text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.dateSubmitted }}
      >
        {strings.timeshift.teamMember.requests.table.dateSubmitted}
      </th>
      <th className="px-6 pb-2 pt-4 text-left text-xs font-medium tracking-wider text-secondary-std">
        {strings.timeshift.teamMember.requests.table.requestDetails}
      </th>
      <th
        className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.status }}
      >
        {strings.timeshift.teamMember.requests.table.status}
      </th>
      <th
        className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.action }}
      >
        {strings.timeshift.teamMember.requests.table.action}
      </th>
    </tr>
  </thead>
);

interface RequestsTableRowProps {
  request: RequestRow;
  index: number;
  onViewDetails: (requestId: string) => void;
}

const RequestsTableRow: FC<RequestsTableRowProps> = ({ request, index, onViewDetails }) => {
  const alternateRowStyling = index % 2 === 0 ? 'bg-secondary-std' : 'bg-primary-std';

  return (
    <tr className={alternateRowStyling}>
      <td className="px-4 py-4 text-sm text-primary-std sm:px-6">{formatDateSubmitted(request.dateSubmitted)}</td>
      <td className="px-4 py-4 text-sm text-primary-std sm:px-6">
        <p className="font-semibold">{request.type}</p>
        {request.details && <p>{request.details}</p>}
      </td>
      <td className="px-4 py-4 text-center text-sm sm:px-6">
        <StatusChip status={request.status} />
      </td>
      <td className="px-4 py-4 text-center text-sm sm:px-6">
        <button
          type="button"
          aria-label={`${strings.timeshift.teamMember.requests.table.viewDetails} ${request.type}`}
          className="rounded-md border border-primary-std px-4 py-1.5 text-xs font-medium text-primary-std hover:bg-secondary-std"
          onClick={() => onViewDetails(request.id)}
        >
          {strings.timeshift.teamMember.requests.table.viewDetails}
        </button>
      </td>
    </tr>
  );
};

type RequestsTableProps = {
  requests: RequestRow[];
  onViewDetails: (requestId: string) => void;
};

const TeamMemberRequestTable: FunctionComponent<RequestsTableProps> = ({ requests, onViewDetails }) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('All');
  const [typeFilter, setTypeFilter] = useState<TypeFilterValue>('All');

  const filteredAndSortedRequests = useMemo(() => {
    const submittedTime = (request: RequestRow) => new Date(request.dateSubmitted).getTime();

    return requests
      .filter(request => statusFilter === 'All' || request.status === statusFilter)
      .filter(request => typeFilter === 'All' || request.type === typeFilter)
      .sort((a, b) => {
        const aPending = a.status === RequestStatus.Pending;
        const bPending = b.status === RequestStatus.Pending;

        if (aPending !== bPending) {
          return aPending ? -1 : 1;
        }

        return submittedTime(b) - submittedTime(a);
      });
  }, [requests, statusFilter, typeFilter]);

  const [offset, limit, controls] = usePagination(pageSize, filteredAndSortedRequests.length);
  const paginatedRequests = filteredAndSortedRequests.slice(offset, offset + limit);

  return (
    <div className="w-full overflow-hidden">
      <div className="flex items-center justify-start space-x-3 p-2">
        <p className="text-sm font-semibold text-primary-std">
          {strings.timeshift.teamMember.requests.filters.filter}:
        </p>
        <TableSelectFilter
          label={strings.timeshift.teamMember.requests.filters.status}
          value={statusFilter}
          options={STATUS_OPTIONS}
          onChange={setStatusFilter}
        />
        <TableSelectFilter
          label={strings.timeshift.teamMember.requests.filters.requestType}
          value={typeFilter}
          options={TYPE_OPTIONS}
          onChange={setTypeFilter}
        />
      </div>
      <table className="min-w-full table-fixed divide-y divide-primary-std border border-primary-std">
        <RequestsTableHeader />
        <tbody className="divide-y divide-primary-std">
          {paginatedRequests.map((request, index) => (
            <RequestsTableRow key={request.id} request={request} index={index} onViewDetails={onViewDetails} />
          ))}
        </tbody>
      </table>
      {filteredAndSortedRequests.length > 0 ? (
        <PageControls {...controls} />
      ) : (
        <div className="flex w-full justify-center border-t border-primary-std px-6 py-5">
          <p className="py-1 text-xs font-medium uppercase text-primary-std">{tableStrings.nothingToShow}</p>
        </div>
      )}
    </div>
  );
};

export default TeamMemberRequestTable;
